<template>
    <div
        v-if="matchedLicences.length"
        class="pdf-allocations-wrapper"
    >
        <div :class="['allocations-type-header', selectedType]">
            {{ allocationTypes[selectedType].title }}
        </div>
        <div
            v-if="index === 0"
            class="allocations-intro"
        >
            <p>
                Water licences<sup>
                    <footnote-tooltip
                        :footnote-number="21"
                    />
                </sup> and short term use approvals<sup>
                    <footnote-tooltip
                        :footnote-number="20"
                    />,
                    <footnote-tooltip
                        :footnote-number="21"
                    />
                </sup> (collectively, ‘allocations’) for surface water and groundwater in British Columbia are managed
                under the Water Sustainability Act<sup>
                    <footnote-tooltip
                        :footnote-number="10"
                    />
                </sup>. These allocations are authorized by the Ministry of Forests, and the BC Energy Regulator
                (associated with activities regulated under the Oil and Gas Activities Act<sup>
                    <footnote-tooltip
                        :footnote-number="11"
                    />
                </sup>). Existing allocations, and active applications<sup>
                    <footnote-tooltip
                        :footnote-number="22"
                    />
                </sup> for water licences within the query basin, are summarized and listed in the charts and
                tables below.
            </p>
            <h4 class="title is-5 total-annual-volume">
                BC Water Sustainability Act - Water Licences -
                {{ introData.licenceCount }} {{ introData.licencePluralization }},
                {{ introData.totalAllocationVolume | numAutoFormat }} m³ Total Annual Volume<sup
                    class="footnote-tooltip"
                >
                    <footnote-tooltip
                        :footnote-number="9"
                    />
                </sup>
            </h4>
            <hr>
        </div>
        <div
            class="allocations-type-table"
        >
            <div class="licence-type-subheader">
                <p class="is-size-5 has-text-weight-bold">
                    {{ allocationTypes[selectedType].subtitle }}
                </p>
                <p class="is-marginless">
                    {{ allocationTypes[selectedType].description }}
                </p>
            </div>
            <div
                v-if="limitAllocations"
                class="footer"
            >
                The table below has been limited to show the top 500 results based on largest water volumes
            </div>

            <allocations-table
                :licences="matchedLicences"
                :pdf-layout="true"
            />
        </div>
    </div>
</template>

<script>
import FootnoteTooltip from '@foundryspatial/footnote-tooltip';

import AllocationsTable from '@/components/AllocationsTable';

export default {
    components: {
        FootnoteTooltip,
        AllocationsTable,
    },
    props: {
        introData: {
            type: Object,
            required: true,
        },
        licences: {
            type: Array,
            required: true,
        },
        selectedType: {
            type: String,
            required: true,
        },
        index: {
            type: Number,
            required: true,
        },
    },

    data: () => ({
        limitAllocations: false,
        matchedLicences: [],
        allocationTypes: {
            'gw-lic': {
                title: 'Water Licences (Groundwater)',
                subtitle: 'Existing Allocations',
                description: 'Current approved groundwater rights. Volumes are included for reference and are not included in calculations on pages 2, 3, and 4',

            },
            'gw-stu': {
                title: 'Short Term Use Approvals (Groundwater)',
                subtitle: 'Existing Allocations',
                description: 'Active short term use approvals, max. 24-month term. Volumes are included in calculations on pages 2, 3, and 4 unless source is Water Storage Site',

            },
            'gw-app': {
                title: 'Groundwater',
                subtitle: 'Active Applications',
                description: 'Active applications for groundwater licences. Volumes are included for reference and are not included in calculations on pages 2, 3, and 4',

            },
            'sw-lic': {
                title: 'Water Licences (Surface Water)',
                subtitle: 'Existing Allocations',
                description: "Current approved surface water rights. Volumes are included in calculations on pages 2, 3, and 4 unless an 'N' is present in Flag column",

            },
            'sw-stu': {
                title: 'Short Term Use Approvals (Surface Water)',
                subtitle: 'Existing Allocations',
                description: 'Active short term use approvals, max. 24-month term. Volumes are included in calculations on pages 2, 3, and 4 unless source is Water Storage Site',

            },
            'sw-app': {
                title: 'Surface Water',
                subtitle: 'Active Applications',
                description: 'Active applications for surface water licences. Volumes are included for reference and are not included in calculations on pages 2, 3, and 4',

            },
        },
    }),

    created() {
        this.matchLicenceType(this.licences);
    },

    methods: {
        /**
         * Takes in all allocation licences and matches with selectedType. Restricts matched array to top 500 results
         * based on water volume
         *
         * @param {Array} licences - all allocation licences from queried watershed
         */
        matchLicenceType(licences) {
            const matches = licences.filter(licence => licence.lic_type === this.selectedType);
            let sorted = matches.sort((a, b) => b.total_vol - a.total_vol);

            if (sorted.length > 500) {
                this.limitAllocations = true;
                sorted = sorted.splice(1, 500);
            }

            this.matchedLicences = sorted;
        },
    },
};
</script>

<style lang="scss">
.pdf-allocations-wrapper {
    .allocations-type-header {
        font-size: 1.875rem;
        line-height: 1.125;
        font-weight: 600;
        padding: 1.25rem;
        margin-bottom: 0.5em;

        &.gw-lic {
            background-color: $color-allocations-gw-long-light;
        }

        &.gw-stu {
            background-color: $color-allocations-gw-short-light;
        }

        &.gw-app {
            background-color: $color-allocations-gw-app-light;
        }

        &.sw-lic {
            background-color: $color-allocations-sw-long-light;
        }

        &.sw-stu {
            background-color: $color-allocations-sw-short-light;
        }

        &.sw-app {
            background-color: $color-allocations-sw-app-light;
        }
    }

    .allocations-intro {
        .total-annual-volume {
            .footnote-tooltip {
                color: $secondary;
            }
        }
    }

    .allocations-type-table {
        page-break-after: always;
        border-bottom: none;
        margin-bottom: 0;
        padding-bottom: 0;

        .allocations-table {
            padding: 0;
        }
    }

    .licence-type-subheader {
        // the table is overlapping the header ever so slightly - unsure why, but a tiny bottom margin fixes it
        margin-bottom: 2px;
        padding: 1em 0;
        height: fit-content;
    }
}
</style>
